import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, Download, Loader2, FileText, CreditCard, Shield, TrendingUp,
  Package, AlertTriangle, ChevronLeft, ChevronRight, Calendar, Filter,
  ArrowUpDown, ArrowUp, ArrowDown,
} from 'lucide-react';
import { Button, Card, Badge, useToast } from './ui';
import { apiClient, salesService } from '../src/services';
import { ENDPOINTS } from '../src/config/api';
import { usePaymentMethods } from '../src/hooks/usePaymentMethods';

const formatCurrency = (v: number) => new Intl.NumberFormat('fr-BJ', { style: 'currency', currency: 'XOF', maximumFractionDigits: 0 }).format(v);

const defaultDateFrom = () => `${new Date().getFullYear()}-01-01`;
const defaultDateTo = () => new Date().toISOString().split('T')[0];

// Reusable sortable header
const SortTh = ({ label, col, sort, onSort, align }: { label: string; col: string; sort: { key: string; dir: string }; onSort: (col: string) => void; align?: string }) => (
  <th className={`px-4 py-3 text-xs font-medium text-slate-500 uppercase cursor-pointer hover:text-slate-700 select-none ${align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'}`} onClick={() => onSort(col)}>
    {label}
    {sort.key === col
      ? sort.dir === 'asc' ? <ArrowUp className="w-3 h-3 ml-1 inline text-primary-600" /> : <ArrowDown className="w-3 h-3 ml-1 inline text-primary-600" />
      : <ArrowUpDown className="w-3 h-3 ml-1 inline opacity-30" />}
  </th>
);

const PAYMENT_STATUS_LABELS: Record<string, string> = {
  NON_PAYEE: 'Non payée', PARTIELLE: 'Partielle', PAYEE: 'Payée', EN_RETARD: 'En retard',
};
const PAYMENT_STATUS_COLORS: Record<string, string> = {
  NON_PAYEE: 'bg-slate-100 text-slate-700', PARTIELLE: 'bg-yellow-100 text-yellow-800',
  PAYEE: 'bg-green-100 text-green-800', EN_RETARD: 'bg-red-100 text-red-800',
};
const SALE_TYPE_LABELS: Record<string, string> = { VENTE: 'Vente', LOCATION: 'Location', LOA: 'LOA' };
const MODE_LABELS: Record<string, string> = { ESPECES: 'Espèces', CHEQUE: 'Chèque', VIREMENT: 'Virement', MOBILE_MONEY_MTN: 'Mobile Money MTN', MOBILE_MONEY_MOOV: 'Mobile Money Moov' };

type Tab = 'journal-ventes' | 'encaissements' | 'paiements-attente' | 'cautions' | 'synthese';

// =============================================================================
// Paiements en attente de validation
// =============================================================================

/**
 * A PENDING payment already reserves the invoice balance server-side but is not
 * counted in montant_paye, so the invoice still looks unpaid and no new payment
 * can be entered on it. This screen is the single place to unblock them.
 */
const PaiementsEnAttente = ({ onCountChange }: { onCountChange?: (n: number) => void }) => {
  const { toast } = useToast();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`${ENDPOINTS.PAYMENTS}/pending`, { params: { page, page_size: 30 } });
      setData(res.data?.data || []);
      setTotalPages(res.data?.pagination?.total_pages || 1);
      const count = res.data?.pagination?.total_items || 0;
      setTotalItems(count);
      onCountChange?.(count);
    } catch { setData([]); }
    finally { setLoading(false); }
  }, [page, onCountChange]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleValidate = async (p: any) => {
    setValidating(p.id);
    try {
      await salesService.validatePayment(p.id);
      toast(`Paiement ${p.code} validé`, 'success');
      fetchData();
    } catch (err: any) {
      toast(err.message || 'Erreur lors de la validation du paiement', 'error');
    } finally {
      setValidating('');
    }
  };

  const totalMontant = data.reduce((acc, p) => acc + Number(p.montant || 0), 0);

  return (
    <div className="space-y-4">
      <div className="p-4 rounded-lg border border-amber-200 bg-amber-50 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900">
          Ces paiements sont enregistrés mais <strong>pas encore validés</strong> : ils ne sont pas
          comptés dans le montant payé des factures, et ils bloquent toute nouvelle saisie de
          paiement sur ces factures. Validez-les ici plutôt que de les ressaisir.
        </div>
      </div>

      <p className="text-sm text-slate-500">
        {totalItems} paiement{totalItems > 1 ? 's' : ''} en attente — Total affiché : {formatCurrency(totalMontant)}
      </p>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Paiement</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Client</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Facture</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Montant</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 uppercase">Mode</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Enregistré par</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 uppercase">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {loading && <tr><td colSpan={8} className="py-12 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin text-slate-400" /></td></tr>}
            {!loading && data.length === 0 && (
              <tr><td colSpan={8} className="py-8 text-center text-slate-500">Aucun paiement en attente de validation</td></tr>
            )}
            {!loading && data.map((p: any) => (
              <tr key={p.id} className="hover:bg-slate-50">
                <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{p.date_paiement ? new Date(p.date_paiement).toLocaleDateString('fr-FR') : '—'}</td>
                <td className="px-4 py-3 text-slate-600 font-mono text-xs">{p.code}</td>
                <td className="px-4 py-3 text-slate-800 font-medium">{p.client_nom || '—'}</td>
                <td className="px-4 py-3 text-slate-600 font-mono text-xs">{p.facture_code || p.contrat_code || '—'}</td>
                <td className="px-4 py-3 text-right font-mono font-bold text-amber-700">{formatCurrency(Number(p.montant))}</td>
                <td className="px-4 py-3 text-center"><span className="px-2 py-0.5 rounded text-xs bg-slate-100 text-slate-700">{MODE_LABELS[p.mode_paiement] || p.mode_paiement}</span></td>
                <td className="px-4 py-3 text-slate-600 text-xs">{p.enregistre_par_nom || '—'}</td>
                <td className="px-4 py-3 text-right">
                  <Button size="sm" onClick={() => handleValidate(p)} disabled={validating === p.id}>
                    {validating === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Valider'}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {totalPages > 1 && (
          <div className="px-4 py-3 flex justify-between items-center border-t bg-slate-50 text-sm">
            <span className="text-slate-500">Page {page}/{totalPages}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Précédent</Button>
              <Button size="sm" variant="ghost" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Suivant</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// Journal des Ventes
// =============================================================================

const JournalVentes = () => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [dateFrom, setDateFrom] = useState(defaultDateFrom);
  const [dateTo, setDateTo] = useState(defaultDateTo);
  const [typeVente, setTypeVente] = useState('');
  const [statutPaiement, setStatutPaiement] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sort, setSort] = useState<{ key: string; dir: string }>({ key: 'date_facture', dir: 'desc' });
  const toggleSort = (col: string) => { setSort(s => s.key === col ? { key: col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key: col, dir: 'asc' }); setPage(1); };

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page, page_size: 30, sort_by: sort.key, sort_dir: sort.dir };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (typeVente) params.type_vente = typeVente;
      if (statutPaiement) params.statut_paiement = statutPaiement;
      if (debouncedSearch) params.search = debouncedSearch;
      const res = await apiClient.get(`${ENDPOINTS.COMPTABILITE}/journal-ventes`, { params });
      setData(res.data?.data || []);
      setTotalPages(res.data?.pagination?.total_pages || 1);
      setTotalItems(res.data?.pagination?.total_items || 0);
    } catch { setData([]); }
    finally { setLoading(false); }
  }, [page, dateFrom, dateTo, typeVente, statutPaiement, debouncedSearch, sort]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExport = async (fmt: string) => {
    const params: any = { report_type: 'journal_ventes', format: fmt };
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    if (typeVente) params.type_vente = typeVente;
    const res = await apiClient.get(`${ENDPOINTS.COMPTABILITE}/export`, { params, responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a'); a.href = url; a.download = `journal_ventes.${fmt}`; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="bg-white p-4 rounded-lg border border-slate-200 grid grid-cols-2 lg:grid-cols-6 gap-3">
        <div className="relative col-span-2">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input className="pl-8 w-full border-slate-300 rounded-md text-sm py-1.5" placeholder="Rechercher facture, client..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <input type="date" className="border-slate-300 rounded-md text-sm py-1.5" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }} />
        <input type="date" className="border-slate-300 rounded-md text-sm py-1.5" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }} />
        <select className="border-slate-300 rounded-md text-sm py-1.5" value={typeVente} onChange={e => { setTypeVente(e.target.value); setPage(1); }}>
          <option value="">Tous types</option>
          <option value="VENTE">Vente</option><option value="LOCATION">Location</option><option value="LOA">LOA</option>
        </select>
        <select className="border-slate-300 rounded-md text-sm py-1.5" value={statutPaiement} onChange={e => { setStatutPaiement(e.target.value); setPage(1); }}>
          <option value="">Tous statuts</option>
          <option value="NON_PAYEE">Non payée</option><option value="PARTIELLE">Partielle</option><option value="PAYEE">Payée</option><option value="EN_RETARD">En retard</option>
        </select>
      </div>

      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">{totalItems} facture{totalItems > 1 ? 's' : ''}</p>
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" icon={Download} onClick={() => handleExport('xlsx')}>Excel</Button>
          <Button size="sm" variant="secondary" icon={Download} onClick={() => handleExport('csv')}>CSV</Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50">
            <tr>
              <SortTh label="Date" col="date_facture" sort={sort} onSort={toggleSort} />
              <SortTh label="N° Facture" col="code" sort={sort} onSort={toggleSort} />
              <SortTh label="Client" col="client_nom" sort={sort} onSort={toggleSort} />
              <SortTh label="Type" col="type_vente" sort={sort} onSort={toggleSort} align="center" />
              <SortTh label="HT" col="montant_ht" sort={sort} onSort={toggleSort} align="right" />
              <SortTh label="TVA" col="montant_tva" sort={sort} onSort={toggleSort} align="right" />
              <SortTh label="TTC" col="montant_ttc" sort={sort} onSort={toggleSort} align="right" />
              <SortTh label="Payé" col="montant_paye" sort={sort} onSort={toggleSort} align="right" />
              <SortTh label="Restant" col="montant_restant" sort={sort} onSort={toggleSort} align="right" />
              <SortTh label="Statut" col="statut_paiement" sort={sort} onSort={toggleSort} align="center" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {loading && <tr><td colSpan={10} className="py-12 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin text-slate-400" /></td></tr>}
            {!loading && data.length === 0 && <tr><td colSpan={10} className="py-8 text-center text-slate-500">Aucune facture</td></tr>}
            {!loading && data.map((r: any) => (
              <tr key={r.id} className="hover:bg-slate-50">
                <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{new Date(r.date_facture).toLocaleDateString('fr-FR')}</td>
                <td className="px-4 py-3 font-medium text-slate-900">{r.code}</td>
                <td className="px-4 py-3 text-slate-600">
                  {r.client_nom}
                  {r.date_debut_location && <div className="text-[10px] text-blue-600">{new Date(r.date_debut_location).toLocaleDateString('fr-FR')} → {r.date_fin_location ? new Date(r.date_fin_location).toLocaleDateString('fr-FR') : '...'}</div>}
                </td>
                <td className="px-4 py-3 text-center"><span className={`px-2 py-0.5 rounded text-xs font-medium ${r.type_vente === 'VENTE' ? 'bg-emerald-100 text-emerald-800' : r.type_vente === 'LOCATION' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`}>{SALE_TYPE_LABELS[r.type_vente] || r.type_vente}</span></td>
                <td className="px-4 py-3 text-right font-mono">{formatCurrency(r.montant_ht)}</td>
                <td className="px-4 py-3 text-right font-mono text-slate-500">{formatCurrency(r.montant_tva)}</td>
                <td className="px-4 py-3 text-right font-mono font-bold">{formatCurrency(r.montant_ttc)}</td>
                <td className="px-4 py-3 text-right font-mono text-green-700">{formatCurrency(r.montant_paye)}</td>
                <td className="px-4 py-3 text-right font-mono text-red-700 font-bold">{formatCurrency(r.montant_restant)}</td>
                <td className="px-4 py-3 text-center"><span className={`px-2 py-0.5 rounded text-xs font-medium ${PAYMENT_STATUS_COLORS[r.statut_paiement] || 'bg-slate-100'}`}>{PAYMENT_STATUS_LABELS[r.statut_paiement] || r.statut_paiement}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
        {totalPages > 1 && (
          <div className="px-4 py-3 flex justify-between items-center border-t bg-slate-50 text-sm">
            <span className="text-slate-500">Page {page}/{totalPages}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Précédent</Button>
              <Button size="sm" variant="ghost" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Suivant</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// Journal des Encaissements
// =============================================================================

const JournalEncaissements = () => {
  const paymentMethods = usePaymentMethods();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [dateFrom, setDateFrom] = useState(defaultDateFrom);
  const [dateTo, setDateTo] = useState(defaultDateTo);
  const [mode, setMode] = useState('');
  const [sort, setSort] = useState<{ key: string; dir: string }>({ key: 'date_paiement', dir: 'desc' });
  const toggleSort = (col: string) => { setSort(s => s.key === col ? { key: col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key: col, dir: 'asc' }); setPage(1); };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page, page_size: 30, sort_by: sort.key, sort_dir: sort.dir };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (mode) params.mode_paiement = mode;
      const res = await apiClient.get(`${ENDPOINTS.COMPTABILITE}/encaissements`, { params });
      setData(res.data?.data || []);
      setTotalPages(res.data?.pagination?.total_pages || 1);
      setTotalItems(res.data?.pagination?.total_items || 0);
    } catch { setData([]); }
    finally { setLoading(false); }
  }, [page, dateFrom, dateTo, mode, sort]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExport = async (fmt: string) => {
    const params: any = { report_type: 'encaissements', format: fmt };
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    const res = await apiClient.get(`${ENDPOINTS.COMPTABILITE}/export`, { params, responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a'); a.href = url; a.download = `encaissements.${fmt}`; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="bg-white p-4 rounded-lg border border-slate-200 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <input type="date" className="border-slate-300 rounded-md text-sm py-1.5" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }} />
        <input type="date" className="border-slate-300 rounded-md text-sm py-1.5" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }} />
        <select className="border-slate-300 rounded-md text-sm py-1.5" value={mode} onChange={e => { setMode(e.target.value); setPage(1); }}>
          <option value="">Tous modes</option>
          {paymentMethods.map(m => <option key={m.code} value={m.code}>{m.label}</option>)}
        </select>
        <div className="flex gap-2 justify-end">
          <Button size="sm" variant="secondary" icon={Download} onClick={() => handleExport('xlsx')}>Excel</Button>
          <Button size="sm" variant="secondary" icon={Download} onClick={() => handleExport('csv')}>CSV</Button>
        </div>
      </div>

      <p className="text-sm text-slate-500">{totalItems} paiement{totalItems > 1 ? 's' : ''}</p>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50">
            <tr>
              <SortTh label="Date" col="date_paiement" sort={sort} onSort={toggleSort} />
              <SortTh label="Client" col="client_nom" sort={sort} onSort={toggleSort} />
              <SortTh label="Facture" col="facture_code" sort={sort} onSort={toggleSort} />
              <SortTh label="Montant" col="montant" sort={sort} onSort={toggleSort} align="right" />
              <SortTh label="Mode" col="mode_paiement" sort={sort} onSort={toggleSort} align="center" />
              <SortTh label="Statut" col="status" sort={sort} onSort={toggleSort} align="center" />
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Référence</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Enregistré par</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {loading && <tr><td colSpan={8} className="py-12 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin text-slate-400" /></td></tr>}
            {!loading && data.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-slate-500">Aucun encaissement</td></tr>}
            {!loading && data.map((r: any) => (
              <tr key={r.id} className="hover:bg-slate-50">
                <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{r.date_paiement ? new Date(r.date_paiement).toLocaleDateString('fr-FR') : '—'}</td>
                <td className="px-4 py-3 text-slate-800 font-medium">{r.client_nom || '—'}</td>
                <td className="px-4 py-3 text-slate-600 font-mono text-xs">{r.facture_code || '—'}</td>
                <td className="px-4 py-3 text-right font-mono font-bold text-green-700">{formatCurrency(r.montant)}</td>
                <td className="px-4 py-3 text-center"><span className="px-2 py-0.5 rounded text-xs bg-slate-100 text-slate-700">{MODE_LABELS[r.mode_paiement] || r.mode_paiement}</span></td>
                <td className="px-4 py-3 text-center"><span className={`px-2 py-0.5 rounded text-xs font-medium ${r.status === 'VALIDATED' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>{r.status === 'VALIDATED' ? 'Validé' : 'En attente'}</span></td>
                <td className="px-4 py-3 text-slate-500 text-xs">{r.reference || '—'}</td>
                <td className="px-4 py-3 text-slate-600 text-xs">{r.enregistre_par || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {totalPages > 1 && (
          <div className="px-4 py-3 flex justify-between items-center border-t bg-slate-50 text-sm">
            <span className="text-slate-500">Page {page}/{totalPages}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Précédent</Button>
              <Button size="sm" variant="ghost" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Suivant</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// Cautions & Dépôts
// =============================================================================

const CautionsTab = () => {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState<{ key: string; dir: string }>({ key: 'contrat_code', dir: 'desc' });
  const toggleSort = (col: string) => { setSort(s => s.key === col ? { key: col, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key: col, dir: 'asc' }); };
  const [typeContrat, setTypeContrat] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page_size: 100 };
      if (typeContrat) params.type_contrat = typeContrat;
      const res = await apiClient.get(`${ENDPOINTS.COMPTABILITE}/cautions`, { params });
      setData(res.data?.data || []);
    } catch { setData([]); }
    finally { setLoading(false); }
  }, [typeContrat]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExport = async (fmt: string) => {
    const res = await apiClient.get(`${ENDPOINTS.COMPTABILITE}/export`, { params: { report_type: 'cautions', format: fmt }, responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a'); a.href = url; a.download = `cautions.${fmt}`; a.click(); URL.revokeObjectURL(url);
  };

  const totalMontant = data.reduce((s, r) => s + Number(r.montant || 0), 0);
  const totalRetenu = data.reduce((s, r) => s + Number(r.montant_retenu || 0), 0);

  const sortedData = [...data].sort((a, b) => {
    const va = a[sort.key] ?? '';
    const vb = b[sort.key] ?? '';
    const cmp = typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb));
    return sort.dir === 'asc' ? cmp : -cmp;
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="flex gap-3 items-center">
          <select className="border-slate-300 rounded-md text-sm py-1.5" value={typeContrat} onChange={e => setTypeContrat(e.target.value)}>
            <option value="">Tous</option><option value="LOCATION">Location</option><option value="LOA">LOA</option>
          </select>
          <span className="text-sm text-slate-500">{data.length} dépôt{data.length > 1 ? 's' : ''} — Total: {formatCurrency(totalMontant)}</span>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" icon={Download} onClick={() => handleExport('xlsx')}>Excel</Button>
          <Button size="sm" variant="secondary" icon={Download} onClick={() => handleExport('csv')}>CSV</Button>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50">
            <tr>
              <SortTh label="Contrat" col="contrat_code" sort={sort} onSort={toggleSort} />
              <SortTh label="Type" col="type_contrat" sort={sort} onSort={toggleSort} align="center" />
              <SortTh label="Client" col="client_nom" sort={sort} onSort={toggleSort} />
              <SortTh label="Machine" col="machine_nom" sort={sort} onSort={toggleSort} />
              <SortTh label="Montant" col="montant" sort={sort} onSort={toggleSort} align="right" />
              <SortTh label="Payé" col="payee" sort={sort} onSort={toggleSort} align="center" />
              <SortTh label="Restitué" col="restituee" sort={sort} onSort={toggleSort} align="center" />
              <SortTh label="Retenu" col="montant_retenu" sort={sort} onSort={toggleSort} align="right" />
              <SortTh label="Statut" col="statut_contrat" sort={sort} onSort={toggleSort} align="center" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {loading && <tr><td colSpan={9} className="py-12 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin text-slate-400" /></td></tr>}
            {!loading && sortedData.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-slate-500">Aucun dépôt</td></tr>}
            {!loading && sortedData.map((r: any, i: number) => (
              <tr key={i} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-slate-900">{r.contrat_code}</td>
                <td className="px-4 py-3 text-center"><span className={`px-2 py-0.5 rounded text-xs font-medium ${r.type_contrat === 'LOCATION' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`}>{r.type_contrat}</span></td>
                <td className="px-4 py-3 text-slate-600">{r.client_nom}</td>
                <td className="px-4 py-3 text-slate-600 text-xs">{r.machine_nom}</td>
                <td className="px-4 py-3 text-right font-mono font-bold">{formatCurrency(r.montant)}</td>
                <td className="px-4 py-3 text-center">{r.payee ? <span className="text-green-600 font-bold">Oui</span> : <span className="text-slate-400">Non</span>}</td>
                <td className="px-4 py-3 text-center">{r.restituee ? <span className="text-blue-600 font-bold">Oui</span> : <span className="text-slate-400">Non</span>}</td>
                <td className="px-4 py-3 text-right font-mono">{r.montant_retenu > 0 ? formatCurrency(r.montant_retenu) : '—'}</td>
                <td className="px-4 py-3 text-center text-xs text-slate-500">{r.statut_contrat}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// =============================================================================
// Synthèse Comptable
// =============================================================================

const SyntheseTab = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState(defaultDateFrom);
  const [dateTo, setDateTo] = useState(defaultDateTo);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await apiClient.get(`${ENDPOINTS.COMPTABILITE}/synthese`, { params });
      setData(res.data?.data || null);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  if (!data) return <div className="text-center py-8 text-slate-500">Aucune donnée</div>;

  const p = data.produits;
  const e = data.encaissements;
  const c = data.creances;
  const d = data.depots;
  const a = data.actifs;

  return (
    <div className="space-y-4">
      <div className="flex gap-3 items-center">
        <input type="date" className="border-slate-300 rounded-md text-sm py-1.5" value={dateFrom} onChange={ev => setDateFrom(ev.target.value)} />
        <span className="text-slate-400">→</span>
        <input type="date" className="border-slate-300 rounded-md text-sm py-1.5" value={dateTo} onChange={ev => setDateTo(ev.target.value)} />
        <span className="text-sm text-slate-500">
          Période: {data.periode_debut ? new Date(data.periode_debut).toLocaleDateString('fr-FR') : ''} — {data.periode_fin ? new Date(data.periode_fin).toLocaleDateString('fr-FR') : ''}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* PRODUITS */}
        <Card className="bg-emerald-50 border-emerald-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-emerald-100 rounded-lg"><TrendingUp className="w-5 h-5 text-emerald-600" /></div>
            <h3 className="font-bold text-emerald-900 text-lg">Produits (Chiffre d'Affaires)</h3>
          </div>
          <div className="space-y-2 text-sm">
            {Object.entries(p.par_type || {}).map(([type, vals]: [string, any]) => (
              <div key={type} className="flex justify-between">
                <span className="text-emerald-700">CA {SALE_TYPE_LABELS[type] || type}</span>
                <span className="font-bold text-emerald-900">{formatCurrency(vals.ttc)}</span>
              </div>
            ))}
            <div className="border-t border-emerald-200 pt-2 flex justify-between font-bold">
              <span className="text-emerald-800">TOTAL TTC</span>
              <span className="text-emerald-900 text-lg">{formatCurrency(p.total_ttc)}</span>
            </div>
            <div className="flex justify-between text-xs text-emerald-600">
              <span>Total HT: {formatCurrency(p.total_ht)}</span>
              <span>TVA: {formatCurrency(p.total_tva)}</span>
            </div>
          </div>
        </Card>

        {/* ENCAISSEMENTS */}
        <Card className="bg-green-50 border-green-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-green-100 rounded-lg"><CreditCard className="w-5 h-5 text-green-600" /></div>
            <h3 className="font-bold text-green-900 text-lg">Encaissements</h3>
          </div>
          <div className="space-y-2 text-sm">
            {Object.entries(e.par_mode || {}).map(([mode, val]: [string, any]) => (
              <div key={mode} className="flex justify-between">
                <span className="text-green-700">{MODE_LABELS[mode] || mode}</span>
                <span className="font-bold text-green-900">{formatCurrency(val as number)}</span>
              </div>
            ))}
            {e.en_attente > 0 && (
              <div className="flex justify-between text-yellow-700">
                <span>En attente de validation</span>
                <span className="font-medium">{formatCurrency(e.en_attente)}</span>
              </div>
            )}
            <div className="border-t border-green-200 pt-2 flex justify-between font-bold">
              <span className="text-green-800">TOTAL ENCAISSÉ</span>
              <span className="text-green-900 text-lg">{formatCurrency(e.total_encaisse)}</span>
            </div>
          </div>
        </Card>

        {/* CRÉANCES */}
        <Card className="bg-red-50 border-red-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-red-100 rounded-lg"><AlertTriangle className="w-5 h-5 text-red-600" /></div>
            <h3 className="font-bold text-red-900 text-lg">Créances Clients</h3>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-red-700">Factures impayées</span>
              <span className="font-bold text-red-900">{c.nb_factures}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-red-700">Dont en retard</span>
              <span className="font-bold text-red-800">{c.nb_en_retard} ({formatCurrency(c.montant_en_retard)})</span>
            </div>
            <div className="border-t border-red-200 pt-2 flex justify-between font-bold">
              <span className="text-red-800">TOTAL RESTANT DÛ</span>
              <span className="text-red-900 text-lg">{formatCurrency(c.total_restant)}</span>
            </div>
          </div>
        </Card>

        {/* DÉPÔTS */}
        <Card className="bg-blue-50 border-blue-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-blue-100 rounded-lg"><Shield className="w-5 h-5 text-blue-600" /></div>
            <h3 className="font-bold text-blue-900 text-lg">Cautions & Dépôts</h3>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-blue-700">Cautions location</span>
              <span className="font-bold">{formatCurrency(d.cautions_encaissees)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-blue-700">Apports LOA</span>
              <span className="font-bold">{formatCurrency(d.apports_loa)}</span>
            </div>
            <div className="flex justify-between text-xs text-blue-600">
              <span>Restituées: {formatCurrency(d.cautions_restituees)}</span>
              <span>Retenues: {formatCurrency(d.cautions_retenues)}</span>
            </div>
            <div className="border-t border-blue-200 pt-2 flex justify-between font-bold">
              <span className="text-blue-800">TOTAL DÉPÔTS</span>
              <span className="text-blue-900 text-lg">{formatCurrency(d.cautions_encaissees + d.apports_loa)}</span>
            </div>
          </div>
        </Card>
      </div>

      {/* ACTIFS */}
      <Card className="bg-slate-50 border-slate-200">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-slate-200 rounded-lg"><Package className="w-5 h-5 text-slate-600" /></div>
          <h3 className="font-bold text-slate-900 text-lg">Actifs</h3>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-slate-500">Stock valorisé (FIFO)</p>
            <p className="text-xl font-bold text-slate-900">{formatCurrency(a.stock_valorise)}</p>
          </div>
          {Object.entries(a.par_site || {}).map(([site, val]: [string, any]) => (
            <div key={site}>
              <p className="text-slate-500">{site}</p>
              <p className="font-bold text-slate-800">{formatCurrency(val as number)}</p>
            </div>
          ))}
          <div>
            <p className="text-slate-500">Machines en location/LOA</p>
            <p className="font-bold text-slate-800">{a.machines_en_location} location + {a.machines_en_loa} LOA</p>
          </div>
        </div>
      </Card>
    </div>
  );
};

// =============================================================================
// Module Principal
// =============================================================================

const TABS: { id: Tab; label: string; icon: any; gerantAllowed?: boolean; roles?: string[] }[] = [
  { id: 'journal-ventes', label: 'Journal des Ventes', icon: FileText },
  { id: 'encaissements', label: 'Encaissements', icon: CreditCard },
  { id: 'paiements-attente', label: 'Paiements en attente', icon: AlertTriangle, roles: ['ADMIN', 'COMPTABLE'] },
  { id: 'cautions', label: 'Cautions & Dépôts', icon: Shield },
  { id: 'synthese', label: 'Synthèse', icon: TrendingUp, gerantAllowed: true, roles: ['ADMIN', 'GERANT'] },
];

export const ComptabiliteModule = ({ userRole }: { userRole?: string }) => {
  const isGerant = userRole === 'GERANT';
  const visibleTabs = TABS.filter(t => {
    if (isGerant) return !!t.gerantAllowed;
    if (t.roles) return t.roles.includes(userRole || '');
    return true;
  });
  const [activeTab, setActiveTab] = useState<Tab>(isGerant ? 'synthese' : 'journal-ventes');
  const [pendingCount, setPendingCount] = useState(0);
  const canSeePending = visibleTabs.some(t => t.id === 'paiements-attente');

  // Badge on the tab: pending payments block re-entry on their invoices, so the
  // count must be visible without opening the tab.
  useEffect(() => {
    if (!canSeePending) return;
    apiClient
      .get(`${ENDPOINTS.PAYMENTS}/pending`, { params: { page: 1, page_size: 1 } })
      .then(res => setPendingCount(res.data?.pagination?.total_items || 0))
      .catch(() => setPendingCount(0));
  }, [canSeePending]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-slate-800">Comptabilité</h2>
        <p className="text-sm text-slate-500">Journal des ventes, encaissements, cautions et synthèse financière.</p>
      </div>

      {visibleTabs.length > 1 && (
        <div className="border-b border-slate-200">
          <nav className="-mb-px flex space-x-8 overflow-x-auto">
            {visibleTabs.map(tab => {
              const Icon = tab.icon;
              return (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  className={`group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                    activeTab === tab.id ? 'border-primary-500 text-primary-600' : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                  }`}>
                  <Icon className={`-ml-0.5 mr-2 h-5 w-5 ${activeTab === tab.id ? 'text-primary-500' : 'text-slate-400'}`} />
                  {tab.label}
                  {tab.id === 'paiements-attente' && pendingCount > 0 && (
                    <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-800">
                      {pendingCount}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>
      )}

      <div className="min-h-[500px]">
        {activeTab === 'journal-ventes' && <JournalVentes />}
        {activeTab === 'encaissements' && <JournalEncaissements />}
        {activeTab === 'paiements-attente' && <PaiementsEnAttente onCountChange={setPendingCount} />}
        {activeTab === 'cautions' && <CautionsTab />}
        {activeTab === 'synthese' && <SyntheseTab />}
      </div>
    </div>
  );
};
